import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

/**
 * MOMENTUM button system.
 *
 * What changed vs shadcn defaults:
 *   - Tighter size scale (default: h-9 not h-10) — Linear/Cron territory,
 *     not consumer SaaS. The old h-10 was sluggish to look at in dense
 *     tables and toolbars.
 *   - Active state distinct from hover (subtle scale + bg shift) so
 *     trainers feel the button "go" when they press it.
 *   - Focus ring uses ring-2 + ring-offset-2 against the volt --ring
 *     token — visible on cream AND ink without changing logic.
 *   - New `volt` variant: signature CTA (ship-it, launch, publish).
 *     Was being hand-rolled in component code (PR #173, Surface 6).
 *   - `outline` variant uses border-border (token-driven) instead of
 *     border-input — matches the rest of the surface vocabulary.
 *   - Default radius bumped from rounded-md (6px) to rounded-lg (8px)
 *     on lg+ sizes; small/icon stay at rounded-md so they don't look
 *     bulbous in dense rows.
 */
const buttonVariants = cva(
  [
    "inline-flex items-center justify-center gap-2 whitespace-nowrap",
    "rounded-md text-sm font-medium tracking-tight",
    "ring-offset-background transition-[background-color,color,border-color,transform,box-shadow] duration-150",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "disabled:pointer-events-none disabled:opacity-50",
    "active:translate-y-px",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  ].join(" "),
  {
    variants: {
      variant: {
        // Primary action — uses --primary token. On light/dark it's the
        // volt signature; on cream surfaces it's the darkened volt so
        // AA contrast holds.
        default:
          "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/85 shadow-sm",
        // Destructive — same shape, red tint.
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/85 shadow-sm",
        // Outlined — quiet. Token-driven border so it adapts to theme.
        outline:
          "border border-border bg-transparent text-foreground hover:bg-muted/60 active:bg-muted",
        // Secondary — filled neutral. Sits between default and outline
        // in visual weight.
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 active:bg-secondary/70",
        // Ghost — no chrome until hover. The most common workhorse for
        // table-row actions, toolbar icons, etc.
        ghost:
          "text-foreground hover:bg-muted/60 active:bg-muted",
        // Link — inline text-style action.
        link: "text-primary underline-offset-4 hover:underline",
        // VOLT — celebratory CTA. Ship-it, launch, publish. Use sparingly.
        // The hover state nudges it brighter rather than darker so the
        // motion reads as "energy" not "press."
        volt:
          "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110 active:brightness-95 shadow-[0_0_0_1px_hsl(var(--primary)/0.4),0_4px_12px_-2px_hsl(var(--primary)/0.5)]",
      },
      size: {
        // Default: 36px tall. Used to be 40px — too chunky for dense UI.
        default: "h-9 px-4 py-2",
        // Small: 32px. Inline-row actions, dropdown triggers.
        sm: "h-8 rounded-md px-3 text-xs",
        // Large: 44px. Hero / page-level CTAs.
        lg: "h-11 rounded-lg px-6 text-base",
        // Icon: square, default-height. Use `iconSm` in dense rows.
        icon: "h-9 w-9",
        iconSm: "h-7 w-7 rounded",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
